import { Injectable, effect, signal } from '@angular/core';

const THEME_STORAGE_KEY = 'isDarkMode';

/** Reads persisted theme or defaults to dark (mirrors index.html bootstrap script). */
export function getInitialTheme(): boolean {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return true;
  }
  const storedMode = localStorage.getItem(THEME_STORAGE_KEY);
  if (storedMode !== null) {
    try {
      return JSON.parse(storedMode) as boolean;
    } catch {
      return true;
    }
  }
  return true;
}

/** Applies `dark` class on `<html>` — safe to call before Angular bootstraps. */
export function applyThemeToDocument(isDarkMode: boolean): void {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', isDarkMode);
}

/**
 * Service responsible for managing the application's visual theme.
 * Handles persistence to localStorage and responds to system dark mode preferences.
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  /**
   * Reactive state of the current theme.
   * `true` for Dark Mode, `false` for Light Mode.
   */
  readonly isDarkMode = signal<boolean>(getInitialTheme());

  constructor() {
    applyThemeToDocument(this.isDarkMode());

    effect(() => {
      const darkMode = this.isDarkMode();
      applyThemeToDocument(darkMode);
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(darkMode));
    });
  }

  /**
   * Flips the current theme state between Light and Dark.
   */
  toggleDarkMode(): void {
    this.isDarkMode.update((prev) => !prev);
  }

  /**
   * Explicitly sets the application theme.
   * @param mode - Set to true for Dark Mode.
   */
  setDarkMode(mode: boolean): void {
    this.isDarkMode.set(mode);
  }
}
